import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { aitsApi, competitiveExamsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, Swords, Loader2, Plus, Trash2, Clock,
  CheckCircle2, XCircle, AlertTriangle, RefreshCw,
} from 'lucide-react';

const STATUS = {
  pending:   { label: 'Generating', color: 'bg-amber-500', icon: Loader2, anim: true },
  scheduled: { label: 'Scheduled',  color: 'bg-indigo-500', icon: Clock, anim: false },
  live:      { label: 'Live',       color: 'bg-rose-500',   icon: Swords, anim: true },
  closed:    { label: 'Closed',     color: 'bg-slate-500',  icon: CheckCircle2, anim: false },
  failed:    { label: 'Failed',     color: 'bg-rose-600',   icon: XCircle, anim: false },
};

// Default start = +1 hour; default duration = 90 min; default 30 Qs
function defaultStart() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  return d.toISOString().slice(0, 16); // yyyy-mm-ddThh:mm for input[type=datetime-local]
}

export default function AdminAITS() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    examId: 'jee_main',
    title: 'AITS Weekly Mock',
    startsAt: defaultStart(),
    durationMin: 90,
    totalQuestions: 30,
  });

  useEffect(() => { competitiveExamsApi.getAll().then(setExams).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await aitsApi.adminList();
      setTests(r.tests || []);
    } catch (e) { toast.error(e.message || 'Load failed'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll pending tests every 8s
  useEffect(() => {
    const anyPending = tests.some(t => t.status === 'pending');
    if (!anyPending) return;
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [tests, load]);

  const schedule = async () => {
    if (!form.title.trim() || !form.startsAt) {
      toast.error('Title and start time required');
      return;
    }
    setSubmitting(true);
    try {
      await aitsApi.adminSchedule({
        examId: form.examId,
        title: form.title.trim(),
        startsAt: new Date(form.startsAt).toISOString(),
        durationMin: Number(form.durationMin),
        totalQuestions: Number(form.totalQuestions),
      });
      toast.success('Scheduled. Questions generating in background…');
      setForm({ ...form, title: 'AITS Weekly Mock', startsAt: defaultStart() });
      load();
    } catch (e) { toast.error(e.message || 'Schedule failed'); }
    setSubmitting(false);
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}" and all attempts?`)) return;
    try {
      await aitsApi.adminDelete(t.test_id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  if (user && !['super_admin', 'org_admin', 'admin'].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 border border-border rounded-2xl">
          <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <p className="font-bold">Admin access only</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/admin')}>Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="aa-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Swords className="w-5 h-5 text-red-500" />AITS Scheduler
              </h1>
              <p className="text-xs text-muted-foreground">Schedule pan-India tests · questions auto-generated via GPT-4o.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Schedule form */}
        <div className="p-5 rounded-2xl border border-border bg-card" data-testid="aa-form">
          <h3 className="font-bold mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-500" />Schedule new AITS</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Exam</label>
              <Select value={form.examId} onValueChange={(v) => setForm({ ...form, examId: v })}>
                <SelectTrigger data-testid="aa-exam"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {exams.map(e => <SelectItem key={e.exam_id} value={e.exam_id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-muted-foreground">Title</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="aa-title" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Starts at (local)</label>
              <Input type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} data-testid="aa-starts" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Duration (min)</label>
                <Input type="number" min="15" max="240" value={form.durationMin} onChange={(e) => setForm({ ...form, durationMin: e.target.value })} data-testid="aa-duration" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Questions</label>
                <Input type="number" min="5" max="90" value={form.totalQuestions} onChange={(e) => setForm({ ...form, totalQuestions: e.target.value })} data-testid="aa-questions" />
              </div>
            </div>
          </div>
          <Button onClick={schedule} disabled={submitting} className="mt-3 bg-red-500 hover:bg-red-600 text-white" data-testid="aa-schedule-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Schedule test
          </Button>
        </div>

        {/* Existing tests */}
        <div className="p-5 rounded-2xl border border-border bg-card" data-testid="aa-list">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold flex items-center gap-2"><Swords className="w-4 h-4 text-red-500" />All tests</h3>
            <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : tests.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">No tests yet.</p>
          ) : (
            <div className="space-y-2">
              {tests.map((t, i) => {
                const meta = STATUS[t.status] || STATUS.pending;
                const Icon = meta.icon;
                return (
                  <div key={t.test_id} className="p-3 rounded-xl border border-border bg-background/50 flex items-center gap-3" data-testid={`aa-row-${i}`}>
                    <span className={`w-8 h-8 rounded-full ${meta.color} text-white flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${meta.anim ? 'animate-pulse' : ''}`} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{t.title}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {t.exam_name} · {new Date(t.starts_at).toLocaleString()} · {t.duration_min} min · {t.total_questions || '?'} Qs
                      </p>
                    </div>
                    <Badge className={`${meta.color} text-white text-[10px]`} data-testid={`aa-status-${i}`}>{meta.label}</Badge>
                    <Button size="sm" variant="ghost" onClick={() => remove(t)} data-testid={`aa-delete-${i}`}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
